import { Alert, Grid, Stack } from '@mui/material';
import { formatters, ValueInput } from '@utils/formattersV2';
import { HeroBreakdown } from '../../shared/HeroBreakdown';
import { faDollar } from '@fortawesome/pro-solid-svg-icons';
import { clampNegativeToZero } from '@utils/valueTransformers';
import { RslReport } from '../../../api/schema';
import { DonutChartWithTitle } from '../../shared/DonutChartWithTitle';
import { SplineChartWithHeader } from '../../shared/SplineChartWithHeader';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { reportsToSeriesData } from '../../../../../utils/reports-to-series-data';
import { channelNames } from '../../../constants/channels';
import { getReportChannels } from '../../../hooks/getReportChannels';
import { useRslAIInsights } from '../../../hooks/useRslAIInsights';
import { ChartCardContainer } from '../ChartCardContainer';

type SalesMetricsProps = {
  reports: RslReport[];
  selectedReport: RslReport | null;
  highlightsComponent?: React.ReactNode;
};

export const SalesMetrics = (props: SalesMetricsProps) => {
  const { reports, selectedReport } = props;

  const { rslAiInsights: summaryAiInsights } = useRslAIInsights(
    selectedReport,
    'summary',
    null
  );

  if (!selectedReport) {
    return <Alert severity="error">No sales data available</Alert>;
  }

  const reportsWithSalesData = reports.filter(
    report =>
      report.incrementalSalesLift !== null ||
      report.projectedIncrementalSales !== null
  );

  const { inStoreChannel, onlineChannel, combinedChannel } =
    getReportChannels(selectedReport);

  return (
    <Grid container spacing="1rem">
      <Grid mt="1rem" size={12}>
        <AIInsightsCard
          testId="rsl-sales-metrics-ai-insights"
          title="Summary"
          aiInsights={summaryAiInsights ?? undefined}
          highlights={props.highlightsComponent}
        />
      </Grid>

      <Grid size={12}>
        <Stack
          border={1}
          borderColor={unifiedPlatformColorPalette.lightGrey300}
          borderRadius="4px"
          sx={{ p: 2 }}
        >
          <Grid container spacing="1rem">
            <Grid
              size={{
                lg: 4,
                xs: 12,
              }}
              data-testid="hero-incremental-sales-lift"
            >
              <HeroBreakdown
                title="Incremental Sales Lift"
                colorScheme="sales"
                valueFormatter={formatters.lift}
                icon={faDollar}
                overallMetric={{
                  value: clampNegativeToZero(
                    selectedReport.incrementalSalesLift ?? 0
                  ),
                  confidenceLevel:
                    selectedReport.incrementalSalesLiftConfidenceLevel,
                  category: channelNames.combined,
                }}
                subMetrics={[
                  {
                    value: clampNegativeToZero(
                      inStoreChannel?.incrementalSalesLift ?? 0
                    ),
                    confidenceLevel:
                      inStoreChannel?.incrementalSalesLiftConfidenceLevel ??
                      null,
                    category: channelNames.in_store,
                  },
                  {
                    value: clampNegativeToZero(
                      onlineChannel?.incrementalSalesLift ?? 0
                    ),
                    confidenceLevel:
                      onlineChannel?.incrementalSalesLiftConfidenceLevel ??
                      null,
                    category: channelNames.online,
                  },
                ]}
              />
            </Grid>

            <Grid
              size={{
                lg: 4,
                xs: 12,
              }}
              data-testid="donut-projected-incremental-sales"
            >
              <DonutChartWithTitle
                title="Projected Incremental Sales"
                formatter={(value: ValueInput) =>
                  formatters.currency(value, { full: true, decimals: 0 })
                }
                overallValue={combinedChannel?.projectedIncrementalSales}
                data={[
                  {
                    name: channelNames.online,
                    value: onlineChannel?.projectedIncrementalSales ?? 0,
                    color: unifiedPlatformColorPalette.chartDarkGreen,
                  },
                  {
                    name: channelNames.in_store,
                    value: inStoreChannel?.projectedIncrementalSales ?? 0,
                    color: unifiedPlatformColorPalette.chartLightGreen,
                  },
                ]}
              />
            </Grid>

            <Grid
              size={{
                lg: 4,
                xs: 12,
              }}
              data-testid="donut-total-projected-sales"
            >
              <DonutChartWithTitle
                title="Total Projected Sales"
                formatter={(value: ValueInput) =>
                  formatters.currency(value, { full: true, decimals: 0 })
                }
                overallValue={combinedChannel?.totalProjectedSales}
                data={[
                  {
                    name: channelNames.online,
                    value: onlineChannel?.totalProjectedSales ?? 0,
                    color: unifiedPlatformColorPalette.chartDarkGreen,
                  },
                  {
                    name: channelNames.in_store,
                    value: inStoreChannel?.totalProjectedSales ?? 0,
                    color: unifiedPlatformColorPalette.chartLightGreen,
                  },
                ]}
              />
            </Grid>
          </Grid>
        </Stack>
      </Grid>

      <ChartCardContainer>
        <SplineChartWithHeader
          title="Incremental Sales Lift"
          data={reportsToSeriesData({
            reports: reportsWithSalesData,
            valueGetter: r => r.incrementalSalesLift,
          })}
          formatter={formatters.lift}
          yAxisLabelFormatter={(value: ValueInput) =>
            formatters.percentage(value, { decimals: 0 })
          }
          differenceType="lift"
          color={unifiedPlatformColorPalette.chartLightGreen}
          confidenceLevel={
            selectedReport.incrementalSalesLiftConfidenceLevel ?? undefined
          }
          data-testid="rsl-incremental-sales-lift-chart"
        />
      </ChartCardContainer>

      <ChartCardContainer>
        <SplineChartWithHeader
          title="Projected Incremental Sales"
          data={reportsToSeriesData({
            reports: reportsWithSalesData,
            valueGetter: r => r.projectedIncrementalSales,
          })}
          formatter={(value: ValueInput) =>
            formatters.currency(value, { full: true })
          }
          differenceType="standard"
          color={unifiedPlatformColorPalette.chartDarkGreen}
          data-testid="rsl-incremental-sales-chart"
        />
      </ChartCardContainer>

      <ChartCardContainer>
        <SplineChartWithHeader
          title="Impressions"
          data={reportsToSeriesData({
            reports: reportsWithSalesData,
            valueGetter: r => r.impressions,
          })}
          formatter={(value: ValueInput) =>
            formatters.number(value, { full: true })
          }
          yAxisLabelFormatter={(value: ValueInput) =>
            formatters.number(value, { full: true, decimals: 0 })
          }
          differenceType="standard"
          color={unifiedPlatformColorPalette.chartLightGreen}
          data-testid="rsl-impressions-chart"
        />
      </ChartCardContainer>

      <ChartCardContainer>
        <SplineChartWithHeader
          title="Unique Users"
          data={reportsToSeriesData({
            reports: reportsWithSalesData,
            valueGetter: r => r.uniqueUsers,
          })}
          formatter={(value: ValueInput) =>
            formatters.number(value, { full: true })
          }
          yAxisLabelFormatter={(value: ValueInput) =>
            formatters.number(value, { full: true, decimals: 0 })
          }
          differenceType="standard"
          color={unifiedPlatformColorPalette.chartDarkGreen}
          data-testid="rsl-unique-users-chart"
        />
      </ChartCardContainer>
    </Grid>
  );
};
